import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  FileText, AlertCircle, Clock, CheckCircle2, ArrowRight,
  type LucideIcon,
} from 'lucide-react'
import { useAuth } from '../auth/AuthProvider'
import { api } from '../api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'

interface DashboardMetrics {
  invoices: { today: number; last7Days: number; last30Days: number }
  successRate: number        // 0..1
  failedQueueDepth: number
  pendingNavSubmissions: number
}

export function DashboardPage() {
  const { session } = useAuth()

  // Metrics endpoint ships in Wave 2 — until then we render skeletons
  // then zero-stats so the page is live but obviously stub-sourced.
  const { data, isLoading } = useQuery<DashboardMetrics>({
    queryKey: ['admin', 'metrics', session?.integratorId],
    queryFn: async () => {
      try {
        return await api.admin.metrics()
      } catch {
        return {
          invoices: { today: 0, last7Days: 0, last30Days: 0 },
          successRate: 0,
          failedQueueDepth: 0,
          pendingNavSubmissions: 0,
        }
      }
    },
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          {session?.isSystemAdmin
            ? 'All-integrator view (system admin)'
            : `Overview of ${session?.integratorName}`}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard icon={FileText}      title="Invoices today"     value={data?.invoices.today}       loading={isLoading} />
        <MetricCard icon={FileText}      title="Last 7 days"        value={data?.invoices.last7Days}   loading={isLoading} />
        <MetricCard icon={CheckCircle2}  title="Success rate"
                    value={data ? `${(data.successRate * 100).toFixed(1)}%` : undefined} loading={isLoading} />
        <MetricCard icon={AlertCircle}   title="Failed in DLQ"      value={data?.failedQueueDepth}     loading={isLoading}
                    tone={data && data.failedQueueDepth > 0 ? 'destructive' : 'default'} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Pending NAV submissions</CardTitle>
            <CardDescription>Invoices queued for NAV OSA 3.0 submit</CardDescription>
          </CardHeader>
          <CardContent className="flex items-center gap-3">
            <Clock className="h-6 w-6 text-muted-foreground" />
            <span className="text-3xl font-semibold">
              {isLoading ? <Skeleton className="h-8 w-16" /> : (data?.pendingNavSubmissions ?? 0)}
            </span>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick actions</CardTitle>
            <CardDescription>Jump into the common views</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button asChild variant="secondary"><Link to="/invoices">Browse invoices <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            <Button asChild variant="secondary"><Link to="/merchants">Merchants      <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            <Button asChild variant="secondary"><Link to="/callbacks">Callbacks       <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

interface MetricCardProps {
  icon: LucideIcon
  title: string
  value: number | string | undefined
  loading: boolean
  tone?: 'default' | 'destructive'
}
function MetricCard({ icon: Icon, title, value, loading, tone = 'default' }: MetricCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className={tone === 'destructive' ? 'h-4 w-4 text-destructive' : 'h-4 w-4 text-muted-foreground'} />
      </CardHeader>
      <CardContent>
        {loading
          ? <Skeleton className="h-8 w-24" />
          : <div className={tone === 'destructive' ? 'text-2xl font-semibold text-destructive' : 'text-2xl font-semibold'}>
              {value ?? '—'}
            </div>}
      </CardContent>
    </Card>
  )
}
